import { gql } from '@apollo/client';
import { USER_FRAGMENT, USER_OVERVIEW_FRAGMENT } from '../users/user.queries';
import { REQUEST_TYPE_FRAGMENT } from '../request-type/request-type.queries';
import { CLIENT_FRAGMENT } from '../clients/client.queries';
import { MEMBER_FRAGMENT } from '../member/member.queries';
import { ENTITY_FRAGMENT } from '../entities/entity.queries';

export const REQUEST_BASE_OVERVIEW_FRAGMENT = gql`
  fragment RequestBaseOverview on Request {
    _id
    clientFinishedWorking
    completion {
      completed
      total
      percentage
    }
    currStaffIsAssigned
    deadline
    deletedAt
    editable
    name
    notes
    requestGroup {
      _id
    }
    sentAt
    status
    updatedAt
  }
`;

export const REQUEST_OVERVIEW_FRAGMENT = gql`
  fragment RequestOverview on Request {
    ...RequestBaseOverview
    assignedTo {
      _id
      name
      givenName
      familyName
      user {
        ...UserOverview
      }
    }
    createdBy {
      ...UserOverview
    }
    entity {
      _id
      name
    }
    requestType {
      _id
      type
    }
    staffEditorIds {
      _id
    }
    staffViewerIds {
      _id
    }
  }

  ${REQUEST_BASE_OVERVIEW_FRAGMENT}
  ${USER_OVERVIEW_FRAGMENT}
`;

const REQUEST_FRAGMENT = gql`
  fragment RequestDetails on Request {
    ...RequestBaseOverview
    assignedTo {
      ...ClientDetails
    }
    blockHistory {
      updatedAt
    }
    blocks {
      id
      data
      type
    }
    clientEditorIds {
      ...ClientDetails
    }
    clientViewerIds {
      ...ClientDetails
    }
    createdBy {
      ...UserDetails
    }
    entity {
      ...EntityDetails
    }
    lastUpdate {
      updatedAt
      user {
        _id
        name
      }
    }
    postSubmissionRedirectUrl
    requestType {
      ...RequestTypeDetails
    }
    staffEditorIds {
      ...MemberDetails
    }
    staffViewerIds {
      ...MemberDetails
    }
    version
    time
  }

  ${CLIENT_FRAGMENT}
  ${ENTITY_FRAGMENT}
  ${MEMBER_FRAGMENT}
  ${REQUEST_BASE_OVERVIEW_FRAGMENT}
  ${REQUEST_TYPE_FRAGMENT}
  ${USER_FRAGMENT}
`;

export const CLEAR_REQUEST = gql`
  mutation ClearRequest($_id: ID!) {
    clearRequest(_id: $_id) {
      ...RequestDetails
    }
  }

  ${REQUEST_FRAGMENT}
`;

export const DUPLICATE_REQUEST = gql`
  mutation DuplicateRequest($_id: ID!, $clear: Boolean!) {
    duplicateRequest(_id: $_id, clear: $clear) {
      ...RequestDetails
    }
  }

  ${REQUEST_FRAGMENT}
`;

export const GET_REQUEST = gql`
  query GetRequest($_id: ID!) {
    requestById(_id: $_id) {
      ...RequestDetails
    }
  }

  ${REQUEST_FRAGMENT}
`;

export const GET_REQUEST_WITH_TOKEN = gql`
  query GetRequestWithToken($token: String!, $requestId: String!, $ignoreStatus: Boolean) {
    requestByWithToken(token: $token, requestId: $requestId, ignoreStatus: $ignoreStatus) {
      error
      request {
        ...RequestDetails
      }
    }
  }

  ${REQUEST_FRAGMENT}
`;

export const LIST_REQUESTS_AS_STAFF = gql`
  query ListRequestsAsStaff(
    $requestGroup: ID
    $page: Float
    $limit: Float
    $status: [REQUEST_STATUS!]
    $includeArchived: Boolean
  ) {
    listRequestsAsStaff(
      requestGroup: $requestGroup
      page: $page
      limit: $limit
      status: $status
      includeArchived: $includeArchived
    ) {
      includeArchived
      requests {
        ...RequestOverview
      }
      status
      page
      total
    }
  }

  ${REQUEST_OVERVIEW_FRAGMENT}
`;

export const CREATE_REQUEST = gql`
  mutation CreateRequest(
    $assignedTo: ID
    $blocks: [RequestBlockInput!]
    $ccClients: [ID!]
    $deadline: DateTimeISO
    $entityId: ID
    $name: String!
    $notes: String
    $postSubmissionRedirectUrl: String
    $requestType: ID
    $staff: [ID!]
    $time: Float
    $version: String
  ) {
    createRequest(
      assignedTo: $assignedTo
      blocks: $blocks
      ccClients: $ccClients
      deadline: $deadline
      entityId: $entityId
      name: $name
      notes: $notes
      postSubmissionRedirectUrl: $postSubmissionRedirectUrl
      requestType: $requestType
      staff: $staff
      time: $time
      version: $version
    ) {
      ...RequestOverview
    }
  }

  ${REQUEST_OVERVIEW_FRAGMENT}
`;

export const CREATE_REQUEST_FROM_TEMPLATE = gql`
  mutation CreateRequestFromTemplate(
    $assignedTo: ID
    $ccClients: [ID!]
    $deadline: DateTimeISO
    $entityId: ID
    $name: String!
    $notes: String
    $postSubmissionRedirectUrl: String
    $requestType: ID
    $staff: [ID!]
    $templateId: ID!
    $time: Float
  ) {
    createRequestFromTemplate(
      assignedTo: $assignedTo
      ccClients: $ccClients
      deadline: $deadline
      entityId: $entityId
      name: $name
      notes: $notes
      postSubmissionRedirectUrl: $postSubmissionRedirectUrl
      requestType: $requestType
      staff: $staff
      templateId: $templateId
      time: $time
    ) {
      ...RequestOverview
    }
  }

  ${REQUEST_OVERVIEW_FRAGMENT}
`;

export const UPDATE_REQUEST = gql`
  mutation UpdateRequest(
    $_id: ID!
    $assignedTo: ID
    $blocks: [RequestBlockInput!]
    $ccClients: [ID!]
    $clientFinishedWorking: Boolean
    $deadline: DateTimeISO
    $entityId: ID
    $lastUpdatedAt: DateTimeISO
    $name: String
    $notes: String
    $postSubmissionRedirectUrl: String
    $requestType: ID
    $staff: [ID!]
    $status: REQUEST_STATUS
    $time: Float
    $version: String
  ) {
    updateRequest(
      _id: $_id
      assignedTo: $assignedTo
      blocks: $blocks
      ccClients: $ccClients
      clientFinishedWorking: $clientFinishedWorking
      deadline: $deadline
      entityId: $entityId
      lastUpdatedAt: $lastUpdatedAt
      name: $name
      notes: $notes
      postSubmissionRedirectUrl: $postSubmissionRedirectUrl
      requestType: $requestType
      staff: $staff
      status: $status
      time: $time
      version: $version
    ) {
      error
      request {
        ...RequestDetails
      }
      updated
    }
  }

  ${REQUEST_FRAGMENT}
`;

export const UPDATE_REQUEST_AS_CLIENT = gql`
  mutation UpdateRequestAsClient($_id: ID!, $blocks: [RequestBlockInput!]!, $lastUpdatedAt: DateTimeISO!) {
    updateRequestAsClient(_id: $_id, blocks: $blocks, lastUpdatedAt: $lastUpdatedAt) {
      error
      request {
        ...RequestDetails
      }
      updated
    }
  }

  ${REQUEST_FRAGMENT}
`;

export const UPDATE_REQUEST_WITH_TOKEN = gql`
  mutation UpdateRequestWithToken(
    $_id: ID!
    $blocks: [RequestBlockInput!]!
    $lastUpdatedAt: DateTimeISO!
    $token: String!
  ) {
    updateRequestWithToken(_id: $_id, blocks: $blocks, lastUpdatedAt: $lastUpdatedAt, token: $token) {
      error
      request {
        ...RequestDetails
      }
      updated
    }
  }

  ${REQUEST_FRAGMENT}
`;

export const UPDATE_REQUEST_ASSIGNMENT = gql`
  mutation UpdateRequestAssignment($_id: ID!) {
    updateRequestAssignment(_id: $_id) {
      ...RequestOverview
    }
  }

  ${REQUEST_OVERVIEW_FRAGMENT}
`;

export const DELETE_REQUEST = gql`
  mutation DeleteRequest($_id: ID!, $permanent: Boolean) {
    deleteRequest(_id: $_id, permanent: $permanent) {
      ...RequestDetails
    }
  }

  ${REQUEST_FRAGMENT}
`;

export const RESTORE_REQUEST = gql`
  mutation RestoreRequest($_id: ID!) {
    restoreRequest(_id: $_id) {
      ...RequestDetails
    }
  }

  ${REQUEST_FRAGMENT}
`;

export const SEND_REQUEST = gql`
  mutation SendRequest(
    $_id: ID!
    $notify: [REQUEST_NOTIFY_OPTION!]!
    $customMessage: String
    $replyTo: String
    $subject: String
  ) {
    sendRequest(_id: $_id, notify: $notify, customMessage: $customMessage, replyTo: $replyTo, subject: $subject) {
      ...RequestDetails
    }
  }

  ${REQUEST_FRAGMENT}
`;

export const SEND_CUSTOM_REQUEST_MESSAGE = gql`
  mutation SendCustomRequestMessage(
    $_id: ID!
    $subject: String!
    $message: String
    $notify: [REQUEST_NOTIFY_OPTION!]!
    $replyTo: String
    $to: [ID!]
  ) {
    sendCustomRequestMessage(
      _id: $_id
      message: $message
      notify: $notify
      replyTo: $replyTo
      to: $to
      subject: $subject
    ) {
      success
      redirectUrl
    }
  }
`;

export const SEND_CUSTOM_REQUEST_MESSAGE_WITH_TOKEN = gql`
  mutation SendCustomRequestMessageWithToken(
    $_id: ID!
    $subject: String!
    $message: String
    $notify: [REQUEST_NOTIFY_OPTION!]!
    $replyTo: String
    $to: [ID!]
    $token: String!
    $completed: Boolean
  ) {
    sendCustomRequestMessageWithToken(
      _id: $_id
      message: $message
      notify: $notify
      replyTo: $replyTo
      to: $to
      token: $token
      subject: $subject
      completed: $completed
    ) {
      success
      redirectUrl
    }
  }
`;

export const UNSEND_REQUEST = gql`
  mutation UnsendRequest($_id: ID!) {
    unsendRequest(_id: $_id) {
      ...RequestDetails
    }
  }

  ${REQUEST_FRAGMENT}
`;

export const START_REQUEST_DOWNLOAD = gql`
  mutation startRequestDownload(
    $_id: ID!
    $assets: [RequestedAssetInput!]
    $fileName: String
    $requestFileName: String
    $sections: [RequestedAssetInput!]
    $token: String
  ) {
    startRequestDownload(
      _id: $_id
      assets: $assets
      fileName: $fileName
      requestFileName: $requestFileName
      sections: $sections
      token: $token
    )
  }
`;
